import { useState } from "react";
import { useAuthStore } from "@/store/auth";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { formatAmount } from "@/utils/formatAmount";
import { formatDate } from "@/utils/formatDate";
import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/client";
import {
  useSalaryCosts,
  useCreateSalaryCost,
  useDeleteSalaryCost,
  useSetSalaryCostPrimary,
} from "@/features/finances/hooks/useFinances";
import { PageLoader } from "@/components/ui/Spinner";

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
  role: string;
}

export function SalaryCostsPage() {
  const { user: authUser } = useAuthStore();
  const canEdit = authUser?.role === "director";

  const { data: salaryCosts = [], isLoading } = useSalaryCosts();
  const createSalary = useCreateSalaryCost();
  const deleteSalary = useDeleteSalaryCost();
  const setPrimary = useSetSalaryCostPrimary();

  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: () => apiClient.get<{ data: Employee[] }>("/auth/users/").then((r) => r.data.data),
    enabled: canEdit,
  });
  const internalUsers = users.filter((u) => u.role !== "contractor");

  const [form, setForm] = useState({
    employee: "",
    monthly_cost: "",
    effective_from: "",
    effective_to: "",
  });
  const [hasEndDate, setHasEndDate] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!form.employee || !form.monthly_cost || !form.effective_from) {
      setErr("Pracownik, kwota miesięczna i data od są wymagane.");
      return;
    }
    try {
      await createSalary.mutateAsync({
        employee: form.employee,
        monthly_cost: form.monthly_cost,
        effective_from: form.effective_from,
        effective_to: hasEndDate ? (form.effective_to || null) : null,
      } as never);
      setForm({ employee: "", monthly_cost: "", effective_from: "", effective_to: "" });
      setHasEndDate(false);
      setErr(null);
    } catch {
      setErr("Błąd zapisu.");
    }
  };

  // Group salary costs by employee for readability
  const grouped = salaryCosts.reduce<Record<string, typeof salaryCosts>>((acc, sc) => {
    if (!acc[sc.employee_name]) acc[sc.employee_name] = [];
    acc[sc.employee_name].push(sc);
    return acc;
  }, {});

  if (isLoading) return <PageLoader />;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between">
        <div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Koszty płac</h1>
            <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-6" />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Miesięczne koszty wynagrodzeń pracowników — podstawa do kalkulacji kosztów projektów.
          </p>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
        {/* Add form (director only) */}
        {canEdit && (
          <div className="w-full lg:w-72 lg:flex-shrink-0">
            <h2 className="text-base font-bold text-gray-900 dark:text-gray-100 mb-4">Nowy koszt płac</h2>
            <div className="bg-white dark:bg-gray-800 p-4 flex flex-col gap-3">
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Pracownik *</label>
                <select
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                  value={form.employee}
                  onChange={(e) => setForm({ ...form, employee: e.target.value })}
                >
                  <option value="">— wybierz pracownika —</option>
                  {internalUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.first_name} {u.last_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  Koszt miesięczny (PLN) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                  value={form.monthly_cost}
                  onChange={(e) => setForm({ ...form, monthly_cost: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">
                  Obowiązuje od *
                </label>
                <DatePicker
                  value={form.effective_from}
                  onChange={(v) => setForm({ ...form, effective_from: v })}
                />
              </div>
              <div>
                <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400 mb-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={hasEndDate}
                    onChange={(e) => setHasEndDate(e.target.checked)}
                  />
                  Ustaw datę zakończenia
                </label>
                {hasEndDate && (
                  <DatePicker
                    value={form.effective_to}
                    onChange={(v) => setForm({ ...form, effective_to: v })}
                  />
                )}
              </div>
              {err && <p className="text-xs text-red-600">{err}</p>}
              <Button loading={createSalary.isPending} onClick={handleCreate}>
                Zapisz koszt płac
              </Button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="flex-1 min-w-0">
          {Object.keys(grouped).length === 0 ? (
            <div className="text-center py-12 text-gray-400 dark:text-gray-500 text-sm">
              Brak zdefiniowanych kosztów płac.
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {Object.entries(grouped)
                .sort(([a], [b]) => a.localeCompare(b, "pl"))
                .map(([employeeName, costs]) => (
                  <div
                    key={employeeName}
                    className="bg-white dark:bg-gray-800 overflow-x-auto"
                  >
                    <div className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 px-4 py-2.5">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{employeeName}</h3>
                    </div>
                    <table className="w-full text-xs min-w-[400px]">
                      <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                        <tr>
                          <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                            Koszt miesięczny
                          </th>
                          <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                            Spółka
                          </th>
                          <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                            Obowiązuje od
                          </th>
                          <th className="text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                            Obowiązuje do
                          </th>
                          <th className="px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400" />
                          {canEdit && <th className="px-4 py-2 w-16 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400" />}
                        </tr>
                      </thead>
                      <tbody>
                        {costs
                          .slice()
                          .sort((a, b) => b.effective_from.localeCompare(a.effective_from))
                          .map((sc) => (
                            <tr
                              key={sc.id}
                              className="hover:bg-gray-50 dark:hover:bg-gray-700/50 border-b border-gray-100 last:border-0"
                            >
                              <td className="px-4 py-2 font-semibold text-gray-900 dark:text-gray-100">
                                {formatAmount(sc.monthly_cost)} PLN/mies.
                              </td>
                              <td className="px-4 py-2 text-gray-600 dark:text-gray-400">
                                {sc.company_name ?? <span className="text-gray-300">—</span>}
                              </td>
                              <td className="px-4 py-2 text-gray-600 dark:text-gray-400">{formatDate(sc.effective_from)}</td>
                              <td className="px-4 py-2 text-gray-500 dark:text-gray-400">
                                {sc.effective_to ? formatDate(sc.effective_to) : (
                                  <span className="text-xs text-green-600 font-medium">
                                    bezterminowo
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-2 text-center">
                                {sc.is_primary ? (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                                    Główny
                                  </span>
                                ) : canEdit ? (
                                  <button
                                    onClick={() => setPrimary.mutate(sc.id)}
                                    disabled={setPrimary.isPending}
                                    className="text-xs text-emerald-600 hover:underline disabled:opacity-50"
                                  >
                                    Ustaw główny
                                  </button>
                                ) : null}
                              </td>
                              {canEdit && (
                                <td className="px-4 py-2 text-right">
                                  <button
                                    onClick={() => {
                                      if (confirm("Usunąć wpis kosztu płac?"))
                                        deleteSalary.mutate(sc.id);
                                    }}
                                    className="text-xs text-red-500 hover:underline"
                                  >
                                    Usuń
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
